import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, useTheme, useMediaQuery } from '@mui/material';
import moment1 from '../assets/images/moment1.webp';
import moment2 from '../assets/images/moment2.webp';
import moment3 from '../assets/images/moment3.webp';
import moment4 from '../assets/images/moment4.webp';
import moment5 from '../assets/images/moment5.webp';
import moment6 from '../assets/images/sample5.webp';
import moment7 from '../assets/images/sample4.webp';
import moment8 from '../assets/images/moment8.jpg';
import moment9 from '../assets/images/moment9.jpg';
import moment10 from '../assets/images/moment10.jpg';
import moment11 from '../assets/images/moment11.jpg';
import moment12 from '../assets/images/moment6.jpg';
import moment13 from '../assets/images/moment7.jpg';
const moments = [
  {
    image: moment1,
  },
  {
    image: moment2,
  },
  {
    image: moment3,
  },
  {
    image: moment4,
  },
  {
    image: moment5,
  },
  {
    image: moment6,
  },
  {
    image: moment7,
  },
  {
    image: moment8,
  },
  {
    image: moment9,
  },
  {
    image: moment10,
  },
  {
    image: moment11,
  },
  {
    image: moment12,
  },
  {
    image: moment13,
  },
];

function Features() {
  const scrollContainerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let animationId;
    const scrollSpeed = isMobile ? 1.0 : 0.5; // 移动端增加滚动速度
    const cardWidth = isMobile ? 280 + 16 : 400 + 24; // 移动端卡片更窄

    const animate = () => {
      // 桌面端：只有在没有悬停时才自动滚动
      // 移动端：始终自动滚动
      const shouldScroll = isMobile ? true : !isHovered;
      
      if (shouldScroll) {
        const currentScroll = scrollContainer.scrollLeft;
        let newScroll = currentScroll + scrollSpeed;
        
        // 当滚动超过第一组图片的总宽度时，重置到开始
        const firstGroupWidth = moments.length * cardWidth;
        if (newScroll >= firstGroupWidth) {
          newScroll = 0;
        }
        
        scrollContainer.scrollLeft = newScroll;
      }
      
      animationId = requestAnimationFrame(animate);
    };

    // 延迟启动动画，确保DOM完全加载
    const timeoutId = setTimeout(() => {
      animate();
    }, 100);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isHovered, isMobile]);

  return (
    <Box sx={{ 
      py: { xs: 6, md: 12 }, 
      background: 'linear-gradient(45deg, rgba(135, 206, 250, 0.1) 0%, rgba(255, 182, 193, 0.08) 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Container maxWidth="xl">
        {/* 标题部分 */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h2" 
            sx={{ 
              mb: 2, 
              fontWeight: 'bold', 
              color: '#2c3e50',
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Moments to Remember
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#34495e', 
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Capturing the joy, growth, and memorable experiences of our students' Mandarin learning journey
          </Typography>
        </Box>

        {/* 横向滚动容器 */}
        <Box
          ref={scrollContainerRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            display: 'flex',
            overflowX: 'auto', // 允许滚动
            gap: { xs: 2, md: 3 },
            pb: 6,
            '&::-webkit-scrollbar': {
              display: 'none', // 完全隐藏滚动条
            },
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
            scrollBehavior: 'auto', // 确保可以程序化滚动
          }}
        >
          {/* 第一组图片 */}
          {moments.map((moment, index) => (
            <Box
              key={`first-${index}`}
              sx={{
                flex: { xs: '0 0 280px', md: '0 0 400px' },
                background: 'white',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                }
              }}
            >
              {/* 图片部分 */}
              <Box
                sx={{
                  height: '300px',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.3))',
                    pointerEvents: 'none'
                  }
                }}
              >
                <Box
                  component="img"
                  src={moment.image}
                  alt={moment.description}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
              </Box>
            </Box>
          ))}
          
          {/* 第二组图片 - 用于无缝循环 */}
          {moments.map((moment, index) => (
            <Box
              key={`second-${index}`}
              sx={{
                flex: { xs: '0 0 280px', md: '0 0 400px' },
                background: 'white',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                }
              }}
            >
              {/* 图片部分 */}
              <Box
                sx={{
                  height: '300px',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.3))',
                    pointerEvents: 'none'
                  }
                }}
              >
                <Box
                  component="img"
                  src={moment.image}
                  alt={moment.description}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default Features;
